import { motion } from 'framer-motion';
import { AlertCircle, CheckCircle2, Lock, Store, Ticket } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { StatusBadge } from '@/components/ui/status-badge';

/**
 * /p/validar — UI standalone para parceiros externos validarem cod_resgate.
 * Sprint 16d. Sem auth Supabase — autentica via email + token entregue pelo admin
 * + envia POST pra Edge Function publica validar-resgate-parceiro-publico.
 *
 * Email + token ficam em localStorage pra parceiro nao redigitar a cada code.
 * Token plain NUNCA fica em sessionStorage publico do navegador alem disso —
 * limpar storage = pedir token novo.
 */

interface Resultado {
  ok: true;
  cod_resgate: string;
  nom_cliente: string;
  nom_oferta: string;
  val_repasse_parceiro: number;
  dta_utilizacao: string;
  nom_parceiro: string;
}

const STORAGE_KEY = 'octa-parceiro-validacao';

export function ValidarResgateParceiroPage() {
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [codigo, setCodigo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState<Resultado | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [lembrar, setLembrar] = useState(true);

  // Restaura credenciais do localStorage
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const data = JSON.parse(raw) as { email: string; token: string };
        setEmail(data.email ?? '');
        setToken(data.token ?? '');
      }
    } catch {
      /* ignore */
    }
  }, []);

  async function validar(e: React.FormEvent) {
    e.preventDefault();
    setErro(null);
    setResultado(null);

    if (!email || !token || !codigo) {
      setErro('Preencha email, token e codigo.');
      return;
    }

    setEnviando(true);
    try {
      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/validar-resgate-parceiro-publico`;
      const r = await fetch(url, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          email_parceiro: email.trim(),
          token_parceiro: token.trim(),
          cod_resgate: codigo.trim(),
        }),
      });
      const json = (await r.json()) as Resultado | { erro: string };

      if (!r.ok || 'erro' in json) {
        const msg = 'erro' in json ? json.erro : `HTTP ${r.status}`;
        setErro(msg);
        toast.error(msg);
        return;
      }

      setResultado(json);
      toast.success('Codigo validado com sucesso!');
      setCodigo('');

      if (lembrar) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ email, token }));
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setErro(msg);
      toast.error('Falha de rede', { description: msg });
    } finally {
      setEnviando(false);
    }
  }

  function esquecerCredenciais() {
    localStorage.removeItem(STORAGE_KEY);
    setEmail('');
    setToken('');
    toast.success('Credenciais removidas deste dispositivo.');
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-white to-slate-100 p-4">
      <div className="mx-auto max-w-md space-y-5 py-6">
        <header className="space-y-1 text-center">
          <div className="bg-octa-gradient mx-auto flex h-12 w-12 items-center justify-center rounded-2xl text-white shadow-lg">
            <Store className="h-6 w-6" />
          </div>
          <h1 className="text-2xl font-extrabold tracking-tight">Validar resgate</h1>
          <p className="text-muted-foreground text-sm">
            Painel para parceiros confirmarem cupons Octa Pontos.
          </p>
        </header>

        <Card variant="elevated" className="rounded-2xl">
          <CardContent className="space-y-4 p-5">
            <form onSubmit={validar} className="space-y-3">
              <div className="space-y-1">
                <Label htmlFor="email">Email do parceiro</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="contato@restaurante.com.br"
                  required
                />
              </div>

              <div className="space-y-1">
                <Label htmlFor="token" className="flex items-center gap-1">
                  <Lock className="h-3 w-3" />
                  Token de validacao
                </Label>
                <Input
                  id="token"
                  type="password"
                  autoComplete="off"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  placeholder="token entregue pelo admin"
                  required
                />
              </div>

              <div className="space-y-1">
                <Label htmlFor="codigo" className="flex items-center gap-1">
                  <Ticket className="h-3 w-3" />
                  Codigo do cliente
                </Label>
                <Input
                  id="codigo"
                  value={codigo}
                  onChange={(e) => setCodigo(e.target.value.toUpperCase())}
                  placeholder="XXXX-XXXX"
                  className="text-center text-lg font-extrabold tabular-nums tracking-wider"
                  required
                />
              </div>

              <label className="text-muted-foreground flex items-center gap-2 text-xs">
                <input
                  type="checkbox"
                  checked={lembrar}
                  onChange={(e) => setLembrar(e.target.checked)}
                />
                Lembrar email + token neste dispositivo
              </label>

              <Button type="submit" className="w-full" disabled={enviando}>
                {enviando ? 'Validando...' : 'Validar codigo'}
              </Button>
            </form>

            {(email || token) && (
              <button
                type="button"
                onClick={esquecerCredenciais}
                className="text-muted-foreground hover:text-foreground w-full text-center text-xs underline"
              >
                Esquecer credenciais deste dispositivo
              </button>
            )}
          </CardContent>
        </Card>

        {erro && (
          <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}>
            <Card className="border-rose-200 bg-rose-50 dark:border-rose-900 dark:bg-rose-950/40">
              <CardContent className="flex items-start gap-3 p-4 text-sm">
                <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-rose-600" />
                <div>
                  <p className="font-semibold text-rose-900 dark:text-rose-100">
                    Nao foi possivel validar
                  </p>
                  <p className="mt-0.5 text-xs text-rose-700 dark:text-rose-200">{erro}</p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {resultado && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
            <Card className="border-emerald-200 bg-emerald-50 dark:border-emerald-900 dark:bg-emerald-950/40">
              <CardContent className="space-y-3 p-5">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                  <span className="text-overline font-bold uppercase text-emerald-700 dark:text-emerald-300">
                    Codigo valido
                  </span>
                </div>
                <div className="space-y-1">
                  <p className="text-sm">
                    <strong>Cliente:</strong> {resultado.nom_cliente}
                  </p>
                  <p className="text-sm">
                    <strong>Oferta:</strong> {resultado.nom_oferta}
                  </p>
                  <p className="text-sm">
                    <strong>Codigo:</strong>{' '}
                    <span className="font-bold tracking-wider">{resultado.cod_resgate}</span>
                  </p>
                </div>
                <div className="flex items-center justify-between border-t border-emerald-200 pt-2 dark:border-emerald-900">
                  <span className="text-overline text-muted-foreground uppercase">A receber</span>
                  <StatusBadge variant="success" className="text-base">
                    R$ {Number(resultado.val_repasse_parceiro).toFixed(2)}
                  </StatusBadge>
                </div>
                <p className="text-muted-foreground text-center text-[10px]">
                  Repasse mensal automatico. Acompanhe pelo seu contato Octa Pontos.
                </p>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </div>
    </div>
  );
}
